/* eslint-disable react/no-danger */
/**
 * OptionsBody
 * 
 * @Author: Focci
 * @Date: 2023-08-29 10:43:06
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-29 10:43:06
 */

import PropTypes from 'prop-types'
import LinearProgress from '@mui/material/LinearProgress'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import useLogin from '@hook/useLogin'
import { useTranslations } from 'next-intl'
import { ButtonBase } from '@comp/material'
import { filter } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getProgress } from './lib'
import OptionsBodySkeleton from './OptionsBodySkeleton'
import OptonLists from './OptonLists'

export default function OptionsBody({
  startField,
  options,
  isLoading,
  onSubmit = () => {}
}) {
  const tt = useTranslations('track')
  const { isLogin, openSigninDialog } = useLogin()
  const [selectedItems, setSelectedItems] = useState([]) // [{ field, value }, ...]
  const [option, setOption] = useState({}) // 当前视图所需要的数据

  const progressValue = useMemo(
    () => getProgress(selectedItems, options),
    [options, selectedItems]
  )

  // 选择
  const handleChange = useCallback((list) => {
    if(list.next === 'login') {
      if(!isLogin) {
        openSigninDialog()
      } else {
        onSubmit(selectedItems)
      }
    } else if(list.next) {
      setOption(options[list.next])
      setSelectedItems((prev) => [
        ...filter(prev, (f) => f.field !== option.columnName),
        { field: option.columnName, value: list.value },
        { field: list.next, value: null }
      ])
    } else {
      onSubmit(selectedItems)
    }
  }, [isLogin, openSigninDialog, onSubmit, selectedItems, options, option])

  // 上一步
  const handlePrev = useCallback(() => {
    const index = selectedItems.length - 2
    const { field } = selectedItems[index] || []

    if(field) {
      setOption(options[field])
      setSelectedItems((prev) => prev.slice(0, prev.length - 1))
    }
  }, [options, selectedItems])

  useEffect(() => { setSelectedItems([{ field: startField }]) }, [startField])
  useEffect(() => { setOption(options[startField] || {}) }, [startField, options])

  if(isLoading || !option) {
    return (
      <OptionsBodySkeleton />
    )
  }

  return (
    <>
      <h2 
        className="text-f1.4 font-medium mt-8" 
        dangerouslySetInnerHTML={{ __html: option.title }}
      />
      <div className="text-f.9 mt-4">
        {option.meta}
      </div>
      <div className="flex flex-wrap items-start gap-4 mt-8">
        <OptonLists
          option={option} 
          selectedItems={selectedItems}
          onChange={handleChange}
        />
      </div>
      <div className="mt-auto pt-10">
        {selectedItems.length > 1 && (
          <div className="mb-2 text-primary">
            <ButtonBase onClick={handlePrev}>
              <ArrowBackIosNewIcon sx={{ fontSize: '.7rem' }} />
              <span>{tt('prevStep')}</span>
            </ButtonBase>
          </div>
        )}
        <LinearProgress 
          variant="determinate" 
          value={progressValue} 
        />
      </div>
    </>
  )
}

OptionsBody.propTypes = {
  startField: PropTypes.string,
  options: PropTypes.object,
  isLoading: PropTypes.bool,
}
